import { NavigationState } from '@react-navigation/native';
import { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';

import { AllAttractionPropsFragment, Locale } from 'app/generated/hygraph';
import useProduct from 'app/hooks/useProduct';
import { useI18n } from 'app/providers/I18nProvider';
import InsiderServiceMobile from 'app/services/Insider';
import { InsiderPageParams } from 'app/services/Insider.types';
import InsiderServiceWeb from 'app/services/InsiderWeb';

interface RouteInfo {
  name: string;
  params?: {
    productId?: string;
    attractionId?: string;
  };
}

const InsiderService = Platform.OS === 'web' ? InsiderServiceWeb : InsiderServiceMobile;

export default function usePushNotifications() {
  const { locale: $locale } = useI18n();
  const [activeRouteInfo, setActiveRouteInfo] = useState<RouteInfo | null>(null);
  const { productContent } = useProduct({
    productContentKey: activeRouteInfo?.params?.productId || '',
  });

  const attraction = useMemo(() => {
    const attrId = activeRouteInfo?.params?.attractionId;
    if (productContent && attrId) {
      return productContent?.attractions?.find(
        (attr: AllAttractionPropsFragment) => attr.key === attrId
      );
    }
    return null;
  }, [activeRouteInfo?.params?.attractionId, productContent]);

  useEffect(() => {
    InsiderService.init();
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      if (!activeRouteInfo) return;

      const { name, params = {} } = activeRouteInfo;
      const { productId, attractionId } = params;

      const source = `mycitypass ${Platform.OS} app`;

      const pageViewParams: InsiderPageParams = { page_name: name, source };

      if (productId) pageViewParams.product_id = productId;

      if (attractionId) pageViewParams.attraction_key = attractionId;

      const attractionNames = (productContent?.attractions?.map(
        (attr: AllAttractionPropsFragment) => attr.name
      ) || []) as string[];

      const handlerMap: Record<string, () => void> = {
        Destinations: () => InsiderService.handleHomePageView(pageViewParams),
        Product: () => {
          if (productContent) {
            InsiderService.handleProductPageView(
              {
                productID: productContent.key || '',
                name: productContent.name || '',
                imageURL: productContent.hero?.url || '',
                price: 1.0,
                currency: 'USD',
                taxonomy: attractionNames,
                locale: $locale,
              },
              pageViewParams
            );
          }
        },
        AttractionTickets: () => {
          InsiderService.handleListingPageView(attractionNames, {
            ...pageViewParams,
            product_id: productContent?.key || '',
            image_url: productContent?.hero?.url || '',
          });
        },
        ProductOffers: () =>
          InsiderService.handleOffersPageView({
            ...pageViewParams,
            product_id: productContent?.key || '',
          }),
        Attraction: () =>
          InsiderService.handleAttractionPageView({
            ...pageViewParams,
            attraction_key: attraction?.key,
          }),
      };

      const handler = handlerMap[name];

      if (handler) handler();
    };

    handleRouteChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRouteInfo, productContent, attraction]);

  const getActiveRouteInfo = (navigationState: NavigationState): RouteInfo | null => {
    if (!navigationState || !navigationState.routes || navigationState.index == null) {
      return null;
    }

    const route = navigationState.routes[navigationState.index];

    if (route.state) {
      return getActiveRouteInfo(route?.state as NavigationState);
    }

    return {
      name: route.name,
      params: route.params,
    };
  };

  const onScreenChange = (navigationState: NavigationState | undefined) => {
    if (!navigationState) return;

    const routeInfo = getActiveRouteInfo(navigationState);
    if (!routeInfo?.name) return;

    setActiveRouteInfo(routeInfo);
  };

  const onLanguageChange = async (locale: Locale) => {
    await InsiderService.handleModifyUser({ locale });
  };

  const onLogin = (userID: string, email: string) => {
    InsiderService.handleModifyUser({
      email,
      userID,
      locale: $locale,
    });
  };

  const onLogout = () => {
    if (InsiderService?.logout) {
      InsiderService.logout();
    }
  };

  return { onScreenChange, onLanguageChange, onLogout, onLogin };
}
